import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import { SearchableCountrySelect } from '../components/SearchableCountrySelect';
import { Spinner } from '../components/Spinner';
import { COUNTRIES, type CountryOption } from '../data/countries';
import { INDUSTRIES } from '../data/industries';
import { PHONE_DIAL_CODES } from '../data/phoneDialCodes';
import { getComplianceFramework, getCurrencyForCountry } from '../lib/countryCompliance';
import { evaluatePasswordStrength } from '../lib/passwordStrength';
import { supabase } from '../lib/supabase';

function sanitizeText(input: string, maxLength: number = 200): string {
  return input
    .trim()
    .replace(/<[^>]*>/g, '') // strip HTML tags
    .replace(/[<>"'`;]/g, '') // strip dangerous characters
    .slice(0, maxLength); // enforce max length
}

function sanitizeUrl(input: string): string | null {
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return null;
  // Block dangerous URL schemes (not evaluated — prefix checks only)
  /* eslint-disable no-script-url */
  const dangerousScheme =
    trimmed.startsWith('javascript:') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('file:') ||
    trimmed.startsWith('vbscript:');
  /* eslint-enable no-script-url */
  if (dangerousScheme) return null;
  // Add https if no protocol
  const withProtocol =
    trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`;
  // Basic domain validation
  try {
    const url = new URL(withProtocol);
    if (!url.hostname.includes('.')) return null;
    return url.hostname; // store just the hostname, not full URL
  } catch {
    return null;
  }
}

function sanitizePhone(dial: string, national: string): string {
  const digits = national.replace(/\D/g, '').slice(0, 15); // E.164 max is 15 digits
  if (digits.length < 6) return '';
  return `${dial}${digits}`;
}

const PAGE_BG = '#0a0f1e';
const ACCENT = '#00ff88';

type SignupPhase = 'select' | 'personal' | 'business';

type CompanySizeValue = '1' | '2-10' | '11-50' | '51-200' | '200+';

const COMPANY_SIZE_OPTIONS: Array<{ value: CompanySizeValue; label: string }> = [
  { value: '1', label: 'Just me' },
  { value: '2-10', label: '2–10' },
  { value: '11-50', label: '11–50' },
  { value: '51-200', label: '51–200' },
  { value: '200+', label: '200+' },
];

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function parseDialFromStored(full: string | null): { dial: string; national: string } {
  if (!full?.trim()) return { dial: '+1', national: '' };
  const s = full.trim();
  if (!s.startsWith('+')) return { dial: '+1', national: s.replace(/\D/g, '') };
  const sorted = [...PHONE_DIAL_CODES].sort((a, b) => b.dial.length - a.dial.length);
  for (const { dial } of sorted) {
    if (s.startsWith(dial)) {
      return { dial, national: s.slice(dial.length).replace(/\D/g, '') };
    }
  }
  return { dial: '+1', national: s.replace(/\D/g, '') };
}

function IconEnvelopeGreen({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPerson({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeLinecap="round" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconBuilding({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M3 21h18M5 21V7l8-4v18M13 21V11h4v10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 9h.01M9 13h.01M9 17h.01" strokeLinecap="round" />
    </svg>
  );
}

const inputClass =
  'w-full rounded-lg border border-border bg-navy px-3 py-2.5 text-sm text-foreground outline-none transition placeholder:text-muted focus:border-accent focus:ring-1 focus:ring-accent/40';

export default function SignupPage() {
  const [phase, setPhase] = useState<SignupPhase>('select');
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [emailForConfirm, setEmailForConfirm] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [emailResent, setEmailResent] = useState(false);
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const [personalFullName, setPersonalFullName] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [personalPassword, setPersonalPassword] = useState('');
  const [personalCountry, setPersonalCountry] = useState<CountryOption | null>(null);
  const [personalCity, setPersonalCity] = useState('');
  const [personalDial, setPersonalDial] = useState(() => parseDialFromStored(null).dial);
  const [personalNational, setPersonalNational] = useState(() => parseDialFromStored(null).national);

  const [bizName, setBizName] = useState('');
  const [bizOwnerName, setBizOwnerName] = useState('');
  const [bizEmail, setBizEmail] = useState('');
  const [bizPassword, setBizPassword] = useState('');
  const [bizCountry, setBizCountry] = useState<CountryOption | null>(null);
  const [bizIndustry, setBizIndustry] = useState('');
  const [bizCompanySize, setBizCompanySize] = useState<CompanySizeValue | ''>('');
  const [bizRole, setBizRole] = useState('');
  const [bizDial, setBizDial] = useState(() => parseDialFromStored(null).dial);
  const [bizNational, setBizNational] = useState(() => parseDialFromStored(null).national);
  const [bizWebsite, setBizWebsite] = useState('');

  const personalStrength = useMemo(() => evaluatePasswordStrength(personalPassword), [personalPassword]);
  const bizStrength = useMemo(() => evaluatePasswordStrength(bizPassword), [bizPassword]);

  function validatePasswordRules(pwd: string): string | null {
    if (!pwd) return 'Create a password.';
    if (pwd.length < 8) return 'Use at least 8 characters.';
    if (!/[a-z]/.test(pwd) || !/[A-Z]/.test(pwd)) return 'Include upper and lower case letters.';
    if (!/\d/.test(pwd)) return 'Include at least one number.';
    return null;
  }

  function validatePersonal(): boolean {
    const next: Record<string, string> = {};
    if (!personalFullName.trim()) next.fullName = 'Enter your full name.';
    else if (personalFullName.trim().length > 100) next.fullName = 'Name must be under 100 characters.';
    if (!personalEmail.trim()) next.email = 'Enter your email.';
    else if (!isValidEmail(personalEmail)) next.email = 'Enter a valid email address.';
    const pwdErr = validatePasswordRules(personalPassword);
    if (pwdErr) next.password = pwdErr;
    if (!personalCountry) next.country = 'Select your country.';
    if (!personalCity.trim()) next.city = 'Enter your city.';
    const digits = personalNational.replace(/\D/g, '');
    if (digits.length < 6) next.phone = 'Enter a valid phone number.';
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  function validateBusiness(): boolean {
    const next: Record<string, string> = {};
    if (!bizName.trim()) next.businessName = 'Enter your business name.';
    else if (bizName.trim().length > 150) next.businessName = 'Business name must be under 150 characters.';
    if (!bizOwnerName.trim()) next.ownerName = 'Enter your full name.';
    else if (bizOwnerName.trim().length > 100) next.ownerName = 'Name must be under 100 characters.';
    if (!bizEmail.trim()) next.email = 'Enter your business email.';
    else if (!isValidEmail(bizEmail)) next.email = 'Enter a valid email address.';
    const pwdErr = validatePasswordRules(bizPassword);
    if (pwdErr) next.password = pwdErr;
    if (!bizCountry) next.country = 'Select your country.';
    if (!bizIndustry) next.industry = 'Select your industry.';
    if (!bizCompanySize) next.companySize = 'Select company size.';
    if (!bizRole.trim()) next.role = 'Enter your role.';
    else if (bizRole.trim().length > 100) next.role = 'Role must be under 100 characters.';
    const digits = bizNational.replace(/\D/g, '');
    if (digits.length < 6) next.phone = 'Enter a valid phone number.';
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  async function submitPersonal(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!validatePersonal() || !personalCountry) return;
    setSubmitting(true);
    const sanitizedPhone = sanitizePhone(personalDial, personalNational);
    if (!sanitizedPhone) {
      setFieldErrors((prev) => ({ ...prev, phone: 'Enter a valid phone number.' }));
      setSubmitting(false);
      return;
    }
    const { data, error } = await supabase.auth.signUp({
      email: personalEmail.trim(),
      password: personalPassword,
      options: {
        data: {
          full_name: sanitizeText(personalFullName, 100),
          account_type: 'personal',
          country: personalCountry.name,
          country_code: personalCountry.code,
          city: sanitizeText(personalCity, 100),
          phone_number: sanitizedPhone,
          owner_name: sanitizeText(personalFullName, 100),
          business_name: sanitizeText(personalFullName, 100),
          industry: 'Individual',
          currency: getCurrencyForCountry(personalCountry.code),
          compliance_framework: getComplianceFramework(personalCountry.code),
        },
      },
    });
    setSubmitting(false);
    if (error) {
      if (error.message.toLowerCase().includes('already registered')) {
        setFormError('This email is already registered. Try signing in instead.');
      } else {
        setFormError(error.message);
      }
      return;
    }
    if (!data.user) {
      setFormError('Could not create your account. Please try again.');
      return;
    }
    setEmailForConfirm(personalEmail.trim());
    setConfirmationSent(true);
  }

  async function submitBusiness(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!validateBusiness() || !bizCountry || !bizCompanySize) return;
    setSubmitting(true);
    const sanitizedBizPhone = sanitizePhone(bizDial, bizNational);
    if (!sanitizedBizPhone) {
      setFieldErrors((prev) => ({ ...prev, phone: 'Enter a valid phone number.' }));
      setSubmitting(false);
      return;
    }
    const { data, error } = await supabase.auth.signUp({
      email: bizEmail.trim(),
      password: bizPassword,
      options: {
        data: {
          business_name: sanitizeText(bizName, 150),
          owner_name: sanitizeText(bizOwnerName, 100),
          account_type: 'business',
          country: bizCountry.name,
          country_code: bizCountry.code,
          industry: bizIndustry, // comes from controlled INDUSTRIES list — safe
          company_size: ['1', '2-10', '11-50', '51-200', '200+'].includes(bizCompanySize)
            ? bizCompanySize
            : null,
          role: sanitizeText(bizRole, 100),
          phone_number: sanitizedBizPhone,
          website: sanitizeUrl(bizWebsite),
          currency: getCurrencyForCountry(bizCountry.code),
          compliance_framework: getComplianceFramework(bizCountry.code),
        },
      },
    });
    setSubmitting(false);
    if (error) {
      if (error.message.toLowerCase().includes('already registered')) {
        setFormError('This email is already registered. Try signing in instead.');
      } else {
        setFormError(error.message);
      }
      return;
    }
    if (!data.user) {
      setFormError('Could not create your account. Please try again.');
      return;
    }
    setEmailForConfirm(bizEmail.trim());
    setConfirmationSent(true);
  }

  function resetAll() {
    setPhase('select');
    setConfirmationSent(false);
    setEmailForConfirm('');
    setFormError(null);
    setFieldErrors({});
    setPersonalFullName('');
    setPersonalEmail('');
    setPersonalPassword('');
    setPersonalCountry(null);
    setPersonalCity('');
    setPersonalDial(parseDialFromStored(null).dial);
    setPersonalNational(parseDialFromStored(null).national);
    setBizName('');
    setBizOwnerName('');
    setBizEmail('');
    setBizPassword('');
    setBizCountry(null);
    setBizIndustry('');
    setBizCompanySize('');
    setBizRole('');
    setBizDial(parseDialFromStored(null).dial);
    setBizNational(parseDialFromStored(null).national);
    setBizWebsite('');
  }

  async function handleResendConfirmation() {
    const em = emailForConfirm.trim();
    if (!em) return;
    setResendError(null);
    setEmailResent(false);
    setResendSubmitting(true);
    const { error } = await supabase.auth.resend({ type: 'signup', email: em });
    setResendSubmitting(false);
    if (error) {
      setResendError(error.message);
      return;
    }
    setEmailResent(true);
  }

  const accentButtonClass =
    'flex w-full min-h-[48px] items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-70';

  return (
    <main className="min-h-screen px-4 py-10 text-foreground sm:px-6 sm:py-14" style={{ backgroundColor: PAGE_BG }}>
      <div
        className={`mx-auto w-full ${!confirmationSent && phase === 'select' ? 'max-w-4xl' : 'max-w-md'}`}
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-6 flex justify-center">
            <Logo size="lg" />
          </div>
          {!confirmationSent && phase === 'select' ? (
            <>
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Create your ShieldSME account</h1>
              <p className="mt-2 text-sm text-muted sm:text-base">Choose how you will use ShieldSME</p>
            </>
          ) : !confirmationSent ? (
            <>
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                {phase === 'personal' ? 'Personal signup' : 'Business signup'}
              </h1>
              <p className="mt-2 text-sm text-muted sm:text-base">
                {phase === 'personal'
                  ? 'For individuals protecting their personal digital life'
                  : 'For companies protecting their business assets'}
              </p>
            </>
          ) : null}
        </div>

        {confirmationSent ? (
          <div className="rounded-2xl border border-border bg-surface/80 p-6 shadow-xl backdrop-blur sm:p-8">
            <div className="flex flex-col items-center text-center">
              <div className="mb-5 flex justify-center" style={{ color: ACCENT }}>
                <IconEnvelopeGreen className="h-12 w-12 shrink-0" />
              </div>
              <h2 className="text-center text-[22px] font-bold" style={{ color: 'var(--text-primary)' }}>
                Check your email
              </h2>
              <p className="mt-4 text-center text-[15px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                We sent a confirmation link to {emailForConfirm.trim()}. Click the link in the email to activate your
                account.
              </p>
              <p className="mt-6 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                Didn&apos;t receive it? Check your spam folder or{' '}
                <button
                  type="button"
                  disabled={resendSubmitting}
                  onClick={() => void handleResendConfirmation()}
                  className="font-medium underline-offset-2 transition hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                  style={{ color: ACCENT }}
                >
                  resend the confirmation email
                </button>
              </p>
              {resendSubmitting ? <p className="mt-2 text-sm text-muted">Sending…</p> : null}
              {emailResent ? (
                <p className="mt-2 text-sm font-medium" style={{ color: ACCENT }} role="status">
                  Email resent!
                </p>
              ) : null}
              {resendError ? (
                <p className="mt-2 text-sm text-danger" role="alert">
                  {resendError}
                </p>
              ) : null}
              <p className="mt-8 text-center text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                Wrong email?{' '}
                <Link
                  to="/signup"
                  className="underline-offset-2 transition hover:underline"
                  style={{ color: 'var(--text-tertiary)' }}
                  onClick={resetAll}
                >
                  Sign up again
                </Link>
              </p>
            </div>
          </div>
        ) : phase === 'select' ? (
          <div className="mx-auto grid max-w-4xl gap-4 sm:grid-cols-2 sm:gap-6">
            <button
              type="button"
              onClick={() => {
                setPhase('personal');
                setFormError(null);
                setFieldErrors({});
              }}
              className="group flex flex-col items-start rounded-2xl border border-border bg-surface/70 p-6 text-left shadow-lg transition hover:border-opacity-100 sm:p-8"
              style={{ outlineColor: ACCENT }}
            >
              <div
                className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl border border-border transition group-hover:border-[color:var(--accent)]"
                style={{ color: ACCENT }}
              >
                <IconPerson className="h-8 w-8" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Personal</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted">
                For individuals protecting their personal digital life
              </p>
              <span className="mt-6 text-sm font-semibold" style={{ color: ACCENT }}>
                Continue →
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setPhase('business');
                setFormError(null);
                setFieldErrors({});
              }}
              className="group flex flex-col items-start rounded-2xl border border-border bg-surface/70 p-6 text-left shadow-lg transition sm:p-8"
            >
              <div
                className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl border border-border transition group-hover:border-[color:var(--accent)]"
                style={{ color: ACCENT }}
              >
                <IconBuilding className="h-8 w-8" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Business</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted">
                For companies protecting their business assets
              </p>
              <span className="mt-6 text-sm font-semibold" style={{ color: ACCENT }}>
                Continue →
              </span>
            </button>
            <p className="text-center text-sm text-muted sm:col-span-2">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold hover:underline" style={{ color: ACCENT }}>
                Sign in
              </Link>
            </p>
          </div>
        ) : phase === 'personal' ? (
          <form
            onSubmit={(e) => void submitPersonal(e)}
            className="rounded-2xl border border-border bg-surface/80 p-6 shadow-xl backdrop-blur sm:p-8"
            noValidate
          >
            <button
              type="button"
              onClick={() => {
                setPhase('select');
                setFormError(null);
                setFieldErrors({});
              }}
              className="mb-4 text-sm font-medium hover:underline"
              style={{ color: ACCENT }}
            >
              ← Back
            </button>
            {formError ? (
              <p className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
                {formError}
              </p>
            ) : null}
            <div className="space-y-4">
              <div>
                <label htmlFor="p-fullname" className="mb-1.5 block text-sm font-medium text-foreground">
                  Full name<span className="text-danger">*</span>
                </label>
                <input
                  id="p-fullname"
                  className={`${inputClass} ${fieldErrors.fullName ? 'border-danger' : ''}`}
                  value={personalFullName}
                  onChange={(e) => setPersonalFullName(e.target.value)}
                  autoComplete="name"
                />
                {fieldErrors.fullName ? <p className="mt-1.5 text-sm text-danger">{fieldErrors.fullName}</p> : null}
              </div>
              <div>
                <label htmlFor="p-email" className="mb-1.5 block text-sm font-medium text-foreground">
                  Email address<span className="text-danger">*</span>
                </label>
                <input
                  id="p-email"
                  type="email"
                  autoComplete="email"
                  className={`${inputClass} ${fieldErrors.email ? 'border-danger' : ''}`}
                  value={personalEmail}
                  onChange={(e) => setPersonalEmail(e.target.value)}
                />
                {fieldErrors.email ? <p className="mt-1.5 text-sm text-danger">{fieldErrors.email}</p> : null}
              </div>
              <div>
                <label htmlFor="p-password" className="mb-1.5 block text-sm font-medium text-foreground">
                  Password<span className="text-danger">*</span>
                </label>
                <input
                  id="p-password"
                  type="password"
                  autoComplete="new-password"
                  className={`${inputClass} ${fieldErrors.password ? 'border-danger' : ''}`}
                  value={personalPassword}
                  onChange={(e) => setPersonalPassword(e.target.value)}
                />
                {personalPassword ? (
                  <div className="mt-2">
                    <div className="mb-1 flex gap-1">
                      {[0, 1, 2, 3].map((i) => (
                        <span
                          key={i}
                          className={`h-1 flex-1 rounded-full ${
                            i < personalStrength.score ? personalStrength.segmentClass : 'bg-border'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-muted">{personalStrength.label}</p>
                  </div>
                ) : null}
                {fieldErrors.password ? <p className="mt-1.5 text-sm text-danger">{fieldErrors.password}</p> : null}
              </div>
              <div>
                <span className="mb-1.5 block text-sm font-medium text-foreground">
                  Country<span className="text-danger">*</span>
                </span>
                <SearchableCountrySelect
                  id="p-country"
                  countries={COUNTRIES}
                  value={personalCountry}
                  onChange={setPersonalCountry}
                  error={fieldErrors.country}
                />
              </div>
              <div>
                <label htmlFor="p-city" className="mb-1.5 block text-sm font-medium text-foreground">
                  City<span className="text-danger">*</span>
                </label>
                <input
                  id="p-city"
                  className={`${inputClass} ${fieldErrors.city ? 'border-danger' : ''}`}
                  value={personalCity}
                  onChange={(e) => setPersonalCity(e.target.value)}
                  autoComplete="address-level2"
                />
                {fieldErrors.city ? <p className="mt-1.5 text-sm text-danger">{fieldErrors.city}</p> : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-[minmax(0,11rem)_1fr]">
                <div>
                  <label htmlFor="p-dial" className="mb-1.5 block text-sm font-medium text-foreground">
                    Dial code<span className="text-danger">*</span>
                  </label>
                  <select
                    id="p-dial"
                    value={personalDial}
                    onChange={(e) => setPersonalDial(e.target.value)}
                    className={`${inputClass} appearance-none bg-navy`}
                  >
                    {PHONE_DIAL_CODES.map((o) => (
                      <option key={`${o.dial}-${o.label}`} value={o.dial}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="p-phone" className="mb-1.5 block text-sm font-medium text-foreground">
                    Phone number<span className="text-danger">*</span>
                  </label>
                  <input
                    id="p-phone"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="712 345 678"
                    className={`${inputClass} ${fieldErrors.phone ? 'border-danger' : ''}`}
                    value={personalNational}
                    onChange={(e) => setPersonalNational(e.target.value)}
                  />
                  {fieldErrors.phone ? <p className="mt-1.5 text-sm text-danger">{fieldErrors.phone}</p> : null}
                </div>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className={accentButtonClass}
              style={{ backgroundColor: ACCENT, color: PAGE_BG }}
            >
              {submitting ? (
                <>
                  <Spinner className="h-5 w-5 text-[#0a0f1e]" />
                  Creating account…
                </>
              ) : (
                'Create Account'
              )}
            </button>
            <p className="mt-4 text-center text-sm text-muted">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold hover:underline" style={{ color: ACCENT }}>
                Sign in
              </Link>
            </p>
          </form>
        ) : (
          <form
            onSubmit={(e) => void submitBusiness(e)}
            className="rounded-2xl border border-border bg-surface/80 p-6 shadow-xl backdrop-blur sm:p-8"
            noValidate
          >
            <button
              type="button"
              onClick={() => {
                setPhase('select');
                setFormError(null);
                setFieldErrors({});
              }}
              className="mb-4 text-sm font-medium hover:underline"
              style={{ color: ACCENT }}
            >
              ← Back
            </button>
            {formError ? (
              <p className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
                {formError}
              </p>
            ) : null}
            <div className="space-y-4">
              <div>
                <label htmlFor="b-name" className="mb-1.5 block text-sm font-medium text-foreground">
                  Business name<span className="text-danger">*</span>
                </label>
                <input
                  id="b-name"
                  className={`${inputClass} ${fieldErrors.businessName ? 'border-danger' : ''}`}
                  value={bizName}
                  onChange={(e) => setBizName(e.target.value)}
                  autoComplete="organization"
                />
                {fieldErrors.businessName ? (
                  <p className="mt-1.5 text-sm text-danger">{fieldErrors.businessName}</p>
                ) : null}
              </div>
              <div>
                <label htmlFor="b-owner" className="mb-1.5 block text-sm font-medium text-foreground">
                  Your full name<span className="text-danger">*</span>
                </label>
                <input
                  id="b-owner"
                  className={`${inputClass} ${fieldErrors.ownerName ? 'border-danger' : ''}`}
                  value={bizOwnerName}
                  onChange={(e) => setBizOwnerName(e.target.value)}
                  autoComplete="name"
                />
                {fieldErrors.ownerName ? <p className="mt-1.5 text-sm text-danger">{fieldErrors.ownerName}</p> : null}
              </div>
              <div>
                <label htmlFor="b-email" className="mb-1.5 block text-sm font-medium text-foreground">
                  Business email<span className="text-danger">*</span>
                </label>
                <input
                  id="b-email"
                  type="email"
                  autoComplete="email"
                  className={`${inputClass} ${fieldErrors.email ? 'border-danger' : ''}`}
                  value={bizEmail}
                  onChange={(e) => setBizEmail(e.target.value)}
                />
                {fieldErrors.email ? <p className="mt-1.5 text-sm text-danger">{fieldErrors.email}</p> : null}
              </div>
              <div>
                <label htmlFor="b-password" className="mb-1.5 block text-sm font-medium text-foreground">
                  Password<span className="text-danger">*</span>
                </label>
                <input
                  id="b-password"
                  type="password"
                  autoComplete="new-password"
                  className={`${inputClass} ${fieldErrors.password ? 'border-danger' : ''}`}
                  value={bizPassword}
                  onChange={(e) => setBizPassword(e.target.value)}
                />
                {bizPassword ? (
                  <div className="mt-2">
                    <div className="mb-1 flex gap-1">
                      {[0, 1, 2, 3].map((i) => (
                        <span
                          key={i}
                          className={`h-1 flex-1 rounded-full ${
                            i < bizStrength.score ? bizStrength.segmentClass : 'bg-border'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-muted">{bizStrength.label}</p>
                  </div>
                ) : null}
                {fieldErrors.password ? <p className="mt-1.5 text-sm text-danger">{fieldErrors.password}</p> : null}
              </div>
              <div>
                <span className="mb-1.5 block text-sm font-medium text-foreground">
                  Country<span className="text-danger">*</span>
                </span>
                <SearchableCountrySelect
                  id="b-country"
                  countries={COUNTRIES}
                  value={bizCountry}
                  onChange={setBizCountry}
                  error={fieldErrors.country}
                />
              </div>
              <div>
                <label htmlFor="b-industry" className="mb-1.5 block text-sm font-medium text-foreground">
                  Industry<span className="text-danger">*</span>
                </label>
                <select
                  id="b-industry"
                  className={`${inputClass} appearance-none bg-navy ${fieldErrors.industry ? 'border-danger' : ''}`}
                  value={bizIndustry}
                  onChange={(e) => setBizIndustry(e.target.value)}
                >
                  <option value="">Select industry</option>
                  {INDUSTRIES.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {fieldErrors.industry ? <p className="mt-1.5 text-sm text-danger">{fieldErrors.industry}</p> : null}
              </div>
              <div>
                <label htmlFor="b-size" className="mb-1.5 block text-sm font-medium text-foreground">
                  Company size<span className="text-danger">*</span>
                </label>
                <select
                  id="b-size"
                  className={`${inputClass} appearance-none bg-navy ${fieldErrors.companySize ? 'border-danger' : ''}`}
                  value={bizCompanySize}
                  onChange={(e) => setBizCompanySize(e.target.value as CompanySizeValue | '')}
                >
                  <option value="">Select size</option>
                  {COMPANY_SIZE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.companySize ? (
                  <p className="mt-1.5 text-sm text-danger">{fieldErrors.companySize}</p>
                ) : null}
              </div>
              <div>
                <label htmlFor="b-role" className="mb-1.5 block text-sm font-medium text-foreground">
                  Your role<span className="text-danger">*</span>
                </label>
                <input
                  id="b-role"
                  className={`${inputClass} ${fieldErrors.role ? 'border-danger' : ''}`}
                  value={bizRole}
                  onChange={(e) => setBizRole(e.target.value)}
                  placeholder="e.g. IT Manager, CEO, Founder"
                />
                {fieldErrors.role ? <p className="mt-1.5 text-sm text-danger">{fieldErrors.role}</p> : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-[minmax(0,11rem)_1fr]">
                <div>
                  <label htmlFor="b-dial" className="mb-1.5 block text-sm font-medium text-foreground">
                    Dial code<span className="text-danger">*</span>
                  </label>
                  <select
                    id="b-dial"
                    value={bizDial}
                    onChange={(e) => setBizDial(e.target.value)}
                    className={`${inputClass} appearance-none bg-navy`}
                  >
                    {PHONE_DIAL_CODES.map((o) => (
                      <option key={`${o.dial}-${o.label}`} value={o.dial}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="b-phone" className="mb-1.5 block text-sm font-medium text-foreground">
                    Phone number<span className="text-danger">*</span>
                  </label>
                  <input
                    id="b-phone"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="712 345 678"
                    className={`${inputClass} ${fieldErrors.phone ? 'border-danger' : ''}`}
                    value={bizNational}
                    onChange={(e) => setBizNational(e.target.value)}
                  />
                  {fieldErrors.phone ? <p className="mt-1.5 text-sm text-danger">{fieldErrors.phone}</p> : null}
                </div>
              </div>
              <div>
                <label htmlFor="b-web" className="mb-1.5 block text-sm font-medium text-foreground">
                  Website / domain <span className="text-muted font-normal">(optional)</span>
                </label>
                <input
                  id="b-web"
                  className={inputClass}
                  value={bizWebsite}
                  onChange={(e) => setBizWebsite(e.target.value)}
                  placeholder="e.g. yourbusiness.com"
                  autoComplete="url"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className={`mt-6 ${accentButtonClass}`}
              style={{ backgroundColor: ACCENT, color: PAGE_BG }}
            >
              {submitting ? (
                <>
                  <Spinner className="h-5 w-5 text-[#0a0f1e]" />
                  Creating account…
                </>
              ) : (
                'Create Account'
              )}
            </button>
            <p className="mt-4 text-center text-sm text-muted">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold hover:underline" style={{ color: ACCENT }}>
                Sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
